import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { buildTheme, ColorMode } from '../theme';

const STORAGE_KEY = 'routing-admin-color-mode';

const preferredMode = (): ColorMode => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    // No choice made yet: follow the OS rather than forcing everyone into the dark.
    return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
};

const ColorModeContext = createContext<{ mode: ColorMode; toggle: () => void }>({
  mode: 'dark',
  toggle: () => undefined
});

/** Owns the colour mode and the theme built from it, so the two can never disagree. */
export const ColorModeProvider = ({
  children,
  initialMode
}: {
  children: ReactNode;
  initialMode?: ColorMode;
}) => {
  const [mode, setMode] = useState<ColorMode>(() => initialMode ?? preferredMode());

  const value = useMemo(
    () => ({
      mode,
      toggle: () =>
        setMode(previous => {
          const next = previous === 'dark' ? 'light' : 'dark';
          try {
            window.localStorage.setItem(STORAGE_KEY, next);
          } catch {
            /* per-browser convenience only */
          }
          return next;
        })
    }),
    [mode]
  );

  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);
